import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, InfoCircle } from '@untitled-ui/icons-react'
import {
  MyButton,
  MyConfirmModal,
  MyDropzone,
  MyLoadingData,
  myToaster,
} from '@interstellar-component'

import { CompanyService } from '../service'
import { GolonganService } from '../wallet.service'
import { golonganReviewBadge } from '../wallet.format'
import { ACCEPTED_EXTENSIONS, MAX_SIZE_BYTES, UMKM_EVIDENCE } from './documents'

/** Info callout under the card (Figma 15564:27308). */
function InfoAlert({ children }) {
  return (
    <div className="flex items-start gap-4 rounded-xl border border-gray/300 bg-white p-4">
      <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-brand/600/10 ring-8 ring-brand/600/[0.06]">
        <InfoCircle className="size-4 text-brand/600" />
      </span>
      <p className="text-sm-regular text-gray/600">{children}</p>
    </div>
  )
}

/** Card shell — heading strip + white body, matching the design's other cards. */
function Card({ title, children, footer }) {
  return (
    <div className="overflow-hidden rounded-xl border border-gray/200 bg-gray/25 shadow-[0px_1px_2px_0px_rgba(10,13,18,0.05)]">
      <div className="px-5 py-3">
        <h2 className="text-sm-semibold text-gray/900">{title}</h2>
      </div>
      <div className="mx-4 mb-4 rounded-xl border border-gray/200 bg-white px-4 pb-5 pt-5">
        {children}
      </div>
      {footer ? (
        <>
          <hr className="border-gray/200" />
          <div className="flex justify-end gap-3 px-5 py-4">{footer}</div>
        </>
      ) : null}
    </div>
  )
}

/**
 * Two error shapes reach this page, and only reading one of them is how the
 * form kept rendering for a company the user cannot touch.
 *
 * `NetworkUtils.get` already unwraps axios: it throws the response BODY as-is
 * (`{ status: 403, message: "You do not have access to this company" }`), so
 * `err.response` is undefined here — the reflex `err.response.status` reads
 * undefined and every guard written that way silently passes. But when the
 * request never got a response at all (network down, timeout) the same helper
 * throws a bare `{ message }` with NO status, and a raw axios error can still
 * arrive from a caller that bypasses the helper. All three are read.
 */
const httpStatusOf = (err) =>
  Number(err?.status ?? err?.response?.status ?? err?.data?.status) || null

const errorMessageOf = (err) =>
  err?.message || err?.data?.message || err?.response?.data?.message || null

/**
 * Turn a failed golongan read into what the page should say.
 *
 * Deliberately fails CLOSED for every status, not just 403: this endpoint is
 * the only thing that tells us which company we are looking at and whether the
 * user may act on it. Until it answers, drawing the dropzones is drawing a
 * loaded gun — pick two files and Submit fires uploadDocument + apply at
 * whatever companyId is in the URL.
 */
function describeLoadError(err) {
  const status = httpStatusOf(err)
  const message = errorMessageOf(err)

  if (status === 403 || status === 401) {
    return {
      title: 'No access to this company',
      message:
        message ||
        'The company in this link does not belong to your holding, so its UMKM application cannot be opened here.',
      retryable: false,
    }
  }
  if (status === 404) {
    return {
      title: 'Company not found',
      message: message || 'The company in this link no longer exists.',
      retryable: false,
    }
  }
  return {
    title: 'Segment status could not be loaded',
    message: `${message || 'Something went wrong while loading the segment status.'} The application form is hidden until the status can be read.`,
    retryable: true,
  }
}

/**
 * "Ajukan sebagai UMKM" — the tenant side of the golongan reclassification
 * (Figma 15557:26980), from empty form through to the waiting room.
 *
 * WHY A PAGE AND NOT A MODAL: the design draws a full screen with its own Back
 * breadcrumb and H1, and the form carries a four-step Coretax walkthrough the
 * user reads WHILE switching to another tab to fetch the document. A modal would
 * be dismissed the moment they click away.
 *
 * ORDER OF OPERATIONS ON SUBMIT — upload every file first, THEN apply. The
 * backend's apply() refuses unless at least one `umkm_`-prefixed document
 * already exists (BR-SUB-06, enforced in the service so the rule holds for every
 * caller), so the reverse order fails 422 every time. That is exactly why the
 * "Ajukan sebagai UMKM" button was dead before this page existed: it called
 * apply() with an empty body and nothing in the product ever created evidence.
 *
 * THE WAITING STATE IS NOT IN FIGMA. No frame on the canvas shows a submitted or
 * under-review application — checked across all 174 top-level frames — yet the
 * alert promises the user 1–3 working days of exactly that. It is built here
 * from the same grammar the rest of the page uses (a card, the review badge the
 * FE already had a colour map for, the evidence list) rather than left blank.
 */
function UmkmApplication() {
  const navigate = useNavigate()
  const { companyId } = useParams()

  const [status, setStatus] = useState(null)
  const [loading, setLoading] = useState(true)
  // Set when getStatus() failed. Its presence — not just its 403-ness — gates
  // the whole form below.
  const [loadError, setLoadError] = useState(null)
  const [files, setFiles] = useState({})
  // Bumped to remount the dropzones — MyDropzone owns its file list internally,
  // so "Reset" cannot clear it by state alone.
  const [formKey, setFormKey] = useState(0)
  const [submitting, setSubmitting] = useState(false)
  const [confirmCancel, setConfirmCancel] = useState(false)

  const load = useCallback(async () => {
    if (!companyId) return
    setLoading(true)
    setLoadError(null)
    try {
      const res = await GolonganService.getStatus(companyId)
      setStatus(res?.data ?? res ?? null)
    } catch (err) {
      // Drop any status from a previous companyId too: a stale `status` would
      // otherwise keep answering isUmkm/inFlight for a company we can no longer
      // read, and the form would branch off data that is not ours.
      setStatus(null)
      setLoadError(describeLoadError(err))
      myToaster(err?.data || err || { status: 400, message: 'Failed to load the segment status' })
    } finally {
      setLoading(false)
    }
  }, [companyId])

  useEffect(() => {
    load()
  }, [load])

  const reviewStatus = status?.golongan_review_status
  const isUmkm = status?.golongan_usaha === 'umkm'
  const inFlight = reviewStatus === 'pending_review' || reviewStatus === 'in_review'
  const wasRejected = reviewStatus === 'rejected'
  const badge = golonganReviewBadge(reviewStatus)

  const ready = useMemo(
    () => UMKM_EVIDENCE.every((d) => !d.required || files[d.key] instanceof File),
    [files],
  )

  const setFile = (key) => (list) =>
    setFiles((prev) => ({ ...prev, [key]: list && list.length > 0 ? list[0] : null }))

  const handleReset = () => {
    setFiles({})
    setFormKey((k) => k + 1)
  }

  const handleSubmit = async () => {
    // `loadError` here is belt-and-braces: the footer that owns this button is
    // already behind the same gate. It stays because the cost of the gate
    // drifting one day is two files and an apply() landing on someone else's
    // company, and the cost of the extra clause is one line.
    if (!ready || submitting || loadError) return
    setSubmitting(true)
    try {
      // Sequential, not Promise.all: a half-uploaded set that then fails apply()
      // leaves orphan evidence rows, and uploading one at a time means the first
      // failure stops the rest instead of scattering partial state.
      const pending = UMKM_EVIDENCE.filter((d) => files[d.key] instanceof File)
      for (let i = 0; i < pending.length; i += 1) {
        const doc = pending[i]
        const fd = new FormData()
        fd.append('file', files[doc.key])
        fd.append('document_type', doc.key)
        fd.append('title', doc.label)
        // eslint-disable-next-line no-await-in-loop
        await CompanyService.uploadDocument(companyId, fd)
      }
      const res = await GolonganService.apply(companyId)
      myToaster(res?.status ? res : { status: 200, message: 'UMKM application submitted' })
      await load()
      handleReset()
    } catch (err) {
      myToaster(err?.data || err || { status: 400, message: 'Failed to submit the application' })
    } finally {
      setSubmitting(false)
    }
  }

  const handleCancel = async () => {
    setSubmitting(true)
    try {
      const res = await GolonganService.cancel(companyId)
      myToaster(res?.status ? res : { status: 200, message: 'UMKM application canceled' })
      await load()
    } catch (err) {
      myToaster(err?.data || err || { status: 400, message: 'Failed to cancel the application' })
    } finally {
      setSubmitting(false)
      setConfirmCancel(false)
    }
  }

  const submitted = status?.documents || []

  return (
    <div className="flex h-full w-full flex-col overflow-y-auto bg-white">
      <div className="mx-auto flex w-full max-w-[1180px] flex-col px-6 py-8">
        <button
          type="button"
          onClick={() => navigate('/company')}
          className="mb-4 flex w-fit items-center gap-2 text-sm-semibold text-gray/600 transition-colors hover:text-gray/900"
        >
          <ArrowLeft className="size-5" />
          Back
        </button>
        <div className="border-b border-gray/200 pb-5">
          <h1 className="text-2xl font-semibold text-gray/900">Apply as UMKM</h1>
        </div>

        {loading && !status ? (
          <div className="py-16">
            <MyLoadingData />
          </div>
        ) : null}

        {/* ── Status golongan tidak terbaca — form TIDAK digambar ───────────
            Toast sendirian tidak cukup: ia hilang beberapa detik kemudian
            sementara judul, dua dropzone, Reset dan Submit tetap berdiri. Di
            situ Submit cuma disabled karena file-nya belum ada — bukan karena
            aksesnya ditolak — jadi memilih dua file menyalakannya lagi dan
            upload + apply terbang ke companyId asing. Pola menyimpan error ke
            state lalu menahan seluruh isi halaman diambil dari SubscribePage. */}
        {!loading && loadError ? (
          <div className="flex flex-col gap-5 py-8">
            <div className="rounded-xl border border-error/200 bg-white">
              <div className="flex items-center gap-2 rounded-t-xl border-b border-error/200 bg-error/50 px-5 py-3">
                <h2 className="text-sm-semibold text-error/800">{loadError.title}</h2>
              </div>
              <div className="px-5 py-4">
                <p className="text-sm-regular text-gray/600">{loadError.message}</p>
                <div className="mt-4 flex gap-3">
                  <MyButton
                    color="secondary"
                    variant="outlined"
                    size="md"
                    type="button"
                    onClick={() => navigate('/company')}
                  >
                    <span className="text-sm-semibold">Back to company list</span>
                  </MyButton>
                  {/* Hanya untuk kegagalan yang bisa berubah kalau diulang.
                      403 tidak akan pernah berubah dengan menekan tombol, dan
                      tombol ulang di sana cuma mengundang orang menabraknya. */}
                  {loadError.retryable ? (
                    <MyButton
                      color="primary"
                      variant="filled"
                      size="md"
                      type="button"
                      onClick={load}
                    >
                      <span className="text-sm-semibold text-white">Try again</span>
                    </MyButton>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        ) : null}

        {/* ── Already UMKM — nothing to apply for ──────────────────────────── */}
        {!loading && !loadError && isUmkm ? (
          <div className="py-8">
            <InfoAlert>
              This company is already classified as UMKM. Plan prices and enquiry fees
              already follow UMKM rates.
            </InfoAlert>
          </div>
        ) : null}

        {/* ── In the queue — the state Figma does not draw ─────────────────── */}
        {!loading && !loadError && !isUmkm && inFlight ? (
          <div className="flex flex-col gap-5 py-8">
            <Card
              title="UMKM application documents"
              footer={
                status?.can_cancel ? (
                  <MyButton
                    color="secondary"
                    variant="outlined"
                    size="md"
                    type="button"
                    disabled={submitting}
                    onClick={() => setConfirmCancel(true)}
                  >
                    <span className="text-sm-semibold">Cancel application</span>
                  </MyButton>
                ) : null
              }
            >
              <div className="flex flex-col gap-4">
                <div className="flex items-center gap-3">
                  {badge ? (
                    <span
                      className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-sm-medium ${badge.cls}`}
                    >
                      {badge.label}
                    </span>
                  ) : null}
                  {/* can_cancel is BE-derived and flips to false the moment a
                      reviewer picks the application up — so it doubles as the
                      honest signal for "you can no longer withdraw". */}
                  {!status?.can_cancel ? (
                    <span className="text-sm-regular text-gray/600">
                      In review — the application can no longer be canceled.
                    </span>
                  ) : null}
                </div>

                <div className="flex flex-col divide-y divide-gray/200 rounded-lg border border-gray/200">
                  {submitted.length === 0 ? (
                    <p className="px-4 py-3 text-sm-regular text-gray/600">
                      No documents recorded.
                    </p>
                  ) : (
                    submitted.map((d) => (
                      <div
                        key={d.document_type}
                        className="flex items-center justify-between gap-4 px-4 py-3"
                      >
                        <div className="min-w-0">
                          <p className="text-sm-medium text-gray/900">{d.title || d.document_type}</p>
                          <p className="truncate text-xs-regular text-gray/500">{d.file_name}</p>
                        </div>
                        <span className="shrink-0 text-xs-medium text-success/700">Submitted</span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </Card>

            <InfoAlert>
              The documents are being verified by the Everest admin (internal portal),
              usually within 1–3 business days. While you wait, the company stays
              Corporate and is still charged Corporate rates — rates already paid are
              not adjusted if the application is approved later.
            </InfoAlert>
          </div>
        ) : null}

        {/* ── The form: never applied, or rejected and trying again ────────── */}
        {!loading && !loadError && !isUmkm && !inFlight ? (
          <div className="flex flex-col gap-5 py-8">
            {wasRejected ? (
              <div className="rounded-xl border border-error/200 bg-white">
                <div className="flex items-center gap-2 rounded-t-xl border-b border-error/200 bg-error/50 px-5 py-3">
                  <h2 className="text-sm-semibold text-error/800">Previous application rejected</h2>
                </div>
                <div className="px-5 py-4">
                  <p className="text-sm-regular text-gray/600">
                    Fix the documents according to the note below, then resubmit.
                  </p>
                  <div className="mt-3 rounded-lg border border-gray/200 bg-gray/50 px-4 py-3">
                    <p className="whitespace-pre-line text-sm-regular text-gray/800">
                      {status?.golongan_review_note || 'No note from the reviewer.'}
                    </p>
                  </div>
                </div>
              </div>
            ) : null}

            <Card
              title="UMKM application documents"
              footer={
                <>
                  <MyButton
                    color="secondary"
                    variant="outlined"
                    size="md"
                    type="button"
                    disabled={submitting}
                    onClick={handleReset}
                  >
                    <span className="text-sm-semibold">Reset</span>
                  </MyButton>
                  {/* Figma draws Submit in full primary fill with one required
                      field still empty. Both fields carry an asterisk, so the
                      button is gated instead — an enabled button that always
                      422s is worse than a disabled one. */}
                  <MyButton
                    color="primary"
                    variant="filled"
                    size="md"
                    type="button"
                    disabled={!ready || submitting}
                    onClick={handleSubmit}
                  >
                    <span className="text-sm-semibold text-white">
                      {submitting ? 'Submitting…' : 'Submit'}
                    </span>
                  </MyButton>
                </>
              }
            >
              <div className="flex flex-col gap-4 lg:flex-row">
                {UMKM_EVIDENCE.map((doc) => (
                  <div key={doc.key} className="flex flex-1 flex-col gap-1.5">
                    <label className="text-sm-medium text-gray/700">
                      {doc.label}
                      {doc.required ? <span className="text-brand/900">&nbsp;*</span> : null}
                    </label>
                    <MyDropzone
                      key={`${doc.key}-${formKey}`}
                      colorBg="bg-white"
                      multiple={false}
                      accept={ACCEPTED_EXTENSIONS}
                      maxSize={MAX_SIZE_BYTES}
                      onChange={setFile(doc.key)}
                      // No thumbnail: a JPG preview grows one column to ~3x the
                      // other and breaks the two-up row the design specifies.
                      showImage={false}
                      // The bar tracks the file being READ into the browser, not
                      // the POST — that only happens on Submit. Leaving it at 0%
                      // reads as a failed upload; the design shows 100% for an
                      // attached-but-unsent file, which is what this is.
                      progressUpload={{ progress: 1 }}
                    />
                    {doc.note ? (
                      <p className="max-w-[320px] text-sm-regular text-gray/600">{doc.note}</p>
                    ) : null}
                    {doc.howTo ? (
                      <div className="mt-2">
                        <p className="text-sm-medium text-gray/700">How to:</p>
                        <ol className="mt-1 list-decimal space-y-1 pl-5 text-sm-regular text-gray/600">
                          {doc.howTo.map((step) => (
                            <li key={step}>{step}</li>
                          ))}
                        </ol>
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>
            </Card>

            <InfoAlert>
              Once submitted, the documents will be verified by the Everest admin
              (internal portal) within 1–3 business days. Your company's status and
              plan prices change to UMKM automatically once the application is
              approved.
            </InfoAlert>
          </div>
        ) : null}
      </div>

      <MyConfirmModal
        negativeActionWord="Back"
        open={confirmCancel}
        onClose={() => setConfirmCancel(false)}
        onConfirm={handleCancel}
        title="Cancel application"
        message="Your UMKM application will be withdrawn. You can apply again at any time."
        positiveActionWord="Cancel application"
        positiveButtonColor="error"
      />
    </div>
  )
}

export default UmkmApplication
